/**
Author - Venkatesh Pullaganti 😃
Git - https://github.com/venkatesh1146
**/

//NOTE: This is theme is only for the mui forms and should not be user for other components
import {createTheme} from '@mui/material/styles';

// Helper function for px to rem conversion
const baseUnit = 14;
const pxToRem = (px) => `${px / baseUnit}rem`;

const DEFAULT_FONT_FAMILY = "'MavenPro', sans-serif";
const DEFAULT_PRIMARY_COLOR = '#6725f4';
const DEFAULT_SECONDARY_COLOR = '#2E2E2E';

const buildTypography = (fontFamily) => {
  return {
    fontFamily,
    fontSize: baseUnit,
    fontWeightLight: 400,
    fontWeightRegular: 400,
    fontWeightMedium: 500,
    fontWeightBold: 700,

    // Heading styles
    h1: {
      fontFamily,
      fontSize: pxToRem(28),
      fontWeight: 700,
      lineHeight: 1.2,
    },
    h2: {
      fontFamily,
      fontSize: pxToRem(24),
      fontWeight: 600,
      lineHeight: 1.2,
    },
    h3: {
      fontFamily,
      fontSize: pxToRem(20),
      fontWeight: 600,
      lineHeight: 1.3,
    },

    // Body styles
    body1: {
      fontFamily,
      fontSize: pxToRem(16),
      fontWeight: 400,
      lineHeight: 1.5,
    },
    body2: {
      fontFamily,
      fontSize: pxToRem(14),
      fontWeight: 400,
      lineHeight: 1.5,
    },

    // Other typography variants
    subtitle1: {
      fontFamily,
      fontSize: pxToRem(16),
      fontWeight: 500,
      lineHeight: 1.5,
    },
    subtitle2: {
      fontFamily,
      fontSize: pxToRem(14),
      fontWeight: 500,
      lineHeight: 1.5,
    },
    button: {
      fontFamily,
      fontSize: pxToRem(16),
      fontWeight: 500,
      lineHeight: 1.4,
      textTransform: 'none',
    },
    caption: {
      fontFamily,
      fontSize: pxToRem(12),
      fontWeight: 400,
      lineHeight: 1.4,
    },
    overline: {
      fontFamily,
      fontSize: pxToRem(12),
      fontWeight: 500,
      lineHeight: 1.5,
      textTransform: 'uppercase',
      letterSpacing: '0.5px',
    },
  };
};

/**
 * Builds the MUI theme used by mui-forms-rendered screens. `mergedTheme` is
 * the already white-label-merged styled-components theme (output of
 * `mergeWhiteLabelTheme`) - the MUI palette/font are derived from it rather
 * than needing a separate MUI-shaped config from the API, so one set of
 * brand colors drives both renderers.
 *
 * @param {object|null} mergedTheme - a clientTheme/partnerTheme-shaped object
 * @returns {import('@mui/material/styles').Theme}
 */
export const getMuiFormsTheme = (mergedTheme) => {
  const fontFamily = mergedTheme?.typography?.fontFamily?.primary || DEFAULT_FONT_FAMILY;
  const primaryColor = mergedTheme?.colors?.brand?.primary || DEFAULT_PRIMARY_COLOR;
  const secondaryColor = mergedTheme?.colors?.brand?.secondary || DEFAULT_SECONDARY_COLOR;

  return createTheme({
    palette: {
      primary: {
        main: primaryColor,
      },
      secondary: {
        main: secondaryColor,
      },
      error: {
        main: '#F44336',
      },
      background: {
        default: '#FFFFFF',
        paper: '#F5F5F5',
      },
      text: {
        primary: '#333333',
        secondary: '#666666',
      },
    },
    typography: buildTypography(fontFamily),

    // Spacing
    spacing: (factor) => {
      const spacingValues = {
        1: pxToRem(4),
        2: pxToRem(8),
        3: pxToRem(16),
        4: pxToRem(24),
        6: pxToRem(32),
        8: pxToRem(48),
      };
      return spacingValues[factor] || `${0.25 * factor}rem`;
    },

    // Shape
    shape: {
      borderRadius: 8,
    },

    // Shadows
    shadows: [
      'none',
      '0 1px 3px rgba(0, 0, 0, 0.12), 0 1px 2px rgba(0, 0, 0, 0.24)',
      '0 1px 3px rgba(0, 0, 0, 0.12), 0 1px 2px rgba(0, 0, 0, 0.24)',
      '0 1px 3px rgba(0, 0, 0, 0.12), 0 1px 2px rgba(0, 0, 0, 0.24)',
      '0 3px 6px rgba(0, 0, 0, 0.15), 0 2px 4px rgba(0, 0, 0, 0.12)',
      '0 3px 6px rgba(0, 0, 0, 0.15), 0 2px 4px rgba(0, 0, 0, 0.12)',
      '0 3px 6px rgba(0, 0, 0, 0.15), 0 2px 4px rgba(0, 0, 0, 0.12)',
      '0 3px 6px rgba(0, 0, 0, 0.15), 0 2px 4px rgba(0, 0, 0, 0.12)',
      '0 10px 20px rgba(0, 0, 0, 0.15), 0 3px 6px rgba(0, 0, 0, 0.10)',
      '0 10px 20px rgba(0, 0, 0, 0.15), 0 3px 6px rgba(0, 0, 0, 0.10)',
      '0 10px 20px rgba(0, 0, 0, 0.15), 0 3px 6px rgba(0, 0, 0, 0.10)',
      '0 10px 20px rgba(0, 0, 0, 0.15), 0 3px 6px rgba(0, 0, 0, 0.10)',
      '0 10px 20px rgba(0, 0, 0, 0.15), 0 3px 6px rgba(0, 0, 0, 0.10)',
      ...Array(12).fill('0 10px 20px rgba(0, 0, 0, 0.15), 0 3px 6px rgba(0, 0, 0, 0.10)'),
    ],

    // Transitions
    transitions: {
      easing: {
        easeInOut: 'cubic-bezier(0.4, 0, 0.2, 1)',
        easeOut: 'cubic-bezier(0.0, 0, 0.2, 1)',
        easeIn: 'cubic-bezier(0.4, 0, 1, 1)',
        sharp: 'cubic-bezier(0.4, 0, 0.6, 1)',
      },
      duration: {
        shortest: 150,
        shorter: 200,
        short: 250,
        standard: 300,
        complex: 375,
        enteringScreen: 225,
        leavingScreen: 195,
      },
    },

    // Components overrides
    components: {
      MuiButton: {
        styleOverrides: {
          root: {
            borderRadius: '8px',
            padding: `${pxToRem(8)} ${pxToRem(16)}`,
            transition: '300ms ease-in-out',
          },
        },
      },
      MuiTextField: {
        styleOverrides: {
          root: {
            marginBottom: pxToRem(16),
          },
        },
      },
      MuiInputBase: {
        styleOverrides: {
          root: {
            fontFamily,
            fontSize: pxToRem(16),
          },
        },
      },
      MuiFormLabel: {
        styleOverrides: {
          root: {
            fontFamily,
            fontSize: pxToRem(14),
          },
        },
      },
    },
  });
};

// Default export kept for any callers not yet migrated to getMuiFormsTheme().
export default getMuiFormsTheme(null);
